import { useCallback, useState } from 'react'

import { ContentCopy, FlightTakeoff, KeyboardArrowDown, LocationOn, Schedule, Store } from '@mui/icons-material'
import { Box, Collapse, Divider, Typography } from '@mui/material'

import Snackbar from '../../../../../../shared/components/Snackbar/Snackbar.functions'
import { PickupDetailsModalProps } from '../../FulfillmentOrderAccordion.types'
import { formatDayNameCapitalized, formatTimeTo12Hour } from './PickupDetailsModal.functions'
import styles from './PickupDetailsModal.styles'

const PickupDetailsModal = (props: PickupDetailsModalProps) => {
  const { pickupCode, logisticsPartnerLocation, flightDayOfWeek } = props

  const [isOpeningTimesExpanded, setIsOpeningTimesExpanded] = useState(false)

  const handleCopyPickupCode = useCallback(() => {
    if (!pickupCode) return

    navigator.clipboard.writeText(pickupCode)

    Snackbar.show({
      message: 'Pickup code copied to clipboard',
      severity: 'success'
    })
  }, [pickupCode])

  const handleToggleOpeningTimes = useCallback(() => {
    setIsOpeningTimesExpanded((previous) => !previous)
  }, [])

  const getFlightDayOpeningTime = (): string => {
    if (!logisticsPartnerLocation?.openingTimes || !flightDayOfWeek) return ''

    const flightDaySchedule = logisticsPartnerLocation.openingTimes.find(
      (daySchedule) => daySchedule.dayName.toLowerCase() === flightDayOfWeek.toLowerCase()
    )

    if (!flightDaySchedule || flightDaySchedule.isClosed) return 'Closed'

    return `${formatTimeTo12Hour(flightDaySchedule.openTime)} - ${formatTimeTo12Hour(flightDaySchedule.closeTime)}`
  }

  const getFlightDayDisplayLabel = (): string => {
    if (!flightDayOfWeek) return 'Today'

    return formatDayNameCapitalized(flightDayOfWeek)
  }

  const renderPickupCodeSection = () => {
    if (!pickupCode) return null

    return (
      <>
        <Box sx={styles.pickupCodeSection}>
          <Typography sx={styles.pickupCodeLabel}>YOUR PICKUP CODE</Typography>

          <Box sx={styles.pickupCodeBox} onClick={handleCopyPickupCode}>
            <Typography sx={styles.pickupCodeText}>{pickupCode}</Typography>
            <ContentCopy sx={styles.copyIcon} />
          </Box>

          <Typography sx={styles.pickupCodeHintText}>Show this code at the pickup counter</Typography>
        </Box>

        <Divider sx={styles.sectionDivider} />
      </>
    )
  }

  const renderLocationSection = () => {
    if (!logisticsPartnerLocation) return null

    return (
      <Box sx={styles.sectionPadding}>
        <Box sx={styles.sectionHeaderContainer}>
          <Box sx={styles.sectionIconWrapper}>
            <LocationOn sx={styles.sectionIcon} />
          </Box>

          <Typography sx={styles.sectionTitleText}>Pickup Location</Typography>
        </Box>

        <Box sx={styles.locationCard}>
          <Box sx={styles.locationRow}>
            <FlightTakeoff sx={styles.locationRowIcon} />

            <Typography sx={styles.locationRowLabelText}>Airport</Typography>

            <Typography sx={styles.locationRowValueText}>
              {logisticsPartnerLocation.airportName} ({logisticsPartnerLocation.airportCode})
            </Typography>
          </Box>

          <Divider sx={styles.locationRowDivider} />

          <Box sx={styles.locationRow}>
            <Store sx={styles.locationRowIcon} />

            <Typography sx={styles.locationRowLabelText}>Partner</Typography>

            <Typography sx={styles.locationRowValueText}>{logisticsPartnerLocation.businessName}</Typography>
          </Box>

          <Divider sx={styles.locationRowDivider} />

          <Box sx={styles.locationRow}>
            <LocationOn sx={styles.locationRowIcon} />

            <Typography sx={styles.locationRowLabelText}>Location</Typography>

            <Typography sx={styles.locationRowValueText}>{logisticsPartnerLocation.locationDetails}</Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderOpeningTimesSection = () => {
    if (!logisticsPartnerLocation?.openingTimes) return null

    const flightDayTime = getFlightDayOpeningTime()
    const flightDayLabel = getFlightDayDisplayLabel()

    return (
      <Box sx={styles.sectionPadding}>
        <Box sx={styles.sectionHeaderContainer}>
          <Box sx={styles.openingTimesHeaderIconWrapper}>
            <Schedule sx={styles.openingTimesHeaderIcon} />
          </Box>

          <Typography sx={styles.sectionTitleText}>Opening Times</Typography>
        </Box>

        <Box sx={styles.collapsibleCard}>
          <Box sx={styles.collapseHeader} onClick={handleToggleOpeningTimes}>
            <Box sx={styles.collapseHeaderLeft}>
              <Typography sx={styles.collapseHeaderDayText}>{flightDayLabel}</Typography>
              <Typography sx={styles.collapseHeaderSubtitleText}>Flight day</Typography>
            </Box>

            <Box sx={styles.collapseHeaderRight}>
              <Typography sx={styles.collapseHeaderTimeText}>{flightDayTime}</Typography>

              <KeyboardArrowDown sx={isOpeningTimesExpanded ? styles.chevronIconExpanded : styles.chevronIcon} />
            </Box>
          </Box>

          <Collapse in={isOpeningTimesExpanded}>
            <Divider sx={styles.expandedBodyDivider} />

            <Box sx={styles.expandedBody}>
              {logisticsPartnerLocation.openingTimes.map((daySchedule) => {
                const isFlightDay = daySchedule.dayName.toLowerCase() === flightDayOfWeek?.toLowerCase()
                const timeDisplay = daySchedule.isClosed
                  ? 'Closed'
                  : `${formatTimeTo12Hour(daySchedule.openTime)} - ${formatTimeTo12Hour(daySchedule.closeTime)}`

                const dayNameStyle = isFlightDay ? styles.flightDayNameText : styles.dayNameText
                const dayTimeStyle = daySchedule.isClosed
                  ? styles.closedTimeText
                  : isFlightDay
                    ? styles.flightDayTimeText
                    : styles.dayTimeText

                return (
                  <Box key={daySchedule.dayName} sx={styles.dayRow}>
                    <Typography sx={dayNameStyle}>{formatDayNameCapitalized(daySchedule.dayName)}</Typography>
                    <Typography sx={dayTimeStyle}>{timeDisplay}</Typography>
                  </Box>
                )
              })}
            </Box>
          </Collapse>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      {renderPickupCodeSection()}
      {renderLocationSection()}
      {logisticsPartnerLocation && <Divider sx={styles.sectionDivider} />}
      {renderOpeningTimesSection()}
    </Box>
  )
}

export default PickupDetailsModal
